import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';
import { Button } from '../../Buttons/Button';
import { DatePicker } from '../../DatePicker';
import { Typography } from '../../Typography';

export interface WebchatDateCardProps {
  /**
   * The message text prompt for date selection
   */
  text: string;
  /**
   * Current date value (YYYY-MM-DD format)
   */
  value?: string;
  /**
   * Callback when date changes
   */
  onChange?: (value: string) => void;
  /**
   * Callback when submit button is clicked
   */
  onSubmit?: (value: string) => void;
  /**
   * Whether to show skip button
   */
  allowSkip?: boolean;
  /**
   * Skip button text
   */
  skipText?: string;
  /**
   * Submit button text
   */
  submitText?: string;
  /**
   * Callback when skip button is clicked
   */
  onSkip?: () => void;
  /**
   * Label for the input field
   */
  label?: string;
  /**
   * Minimum allowed date (YYYY-MM-DD format)
   */
  minDate?: string;
  /**
   * Maximum allowed date (YYYY-MM-DD format)
   */
  maxDate?: string;
  /**
   * Whether the input is disabled
   */
  disabled?: boolean;
  /**
   * Whether the input has been submitted (read-only mode)
   */
  submitted?: boolean;
  /**
   * Avatar URL for bot
   */
  avatarUrl?: string;
  /**
   * Avatar initials for bot
   */
  avatarInitials?: string;
}

const MessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
}));

const Avatar = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.grey[300],
  flexShrink: 0,
  fontSize: '14px',
  fontWeight: 500,
  color: theme.palette.text.primary,
  overflow: 'hidden',
}));

const ActivityCard = styled(Card)(() => ({
  maxWidth: 480,
  minWidth: 250,
  borderRadius: 8,
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.12)',
}));

const MessageText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  marginBottom: theme.spacing(2),
  color: theme.palette.text.primary,
}));

/**
 * WebchatDateCard displays a date selection prompt with native date picker.
 * This component is designed to be a standalone, reusable date card
 * that can be integrated into various chat interfaces.
 */
export const WebchatDateCard: React.FC<WebchatDateCardProps> = ({
  text,
  value: controlledValue,
  onChange,
  onSubmit,
  allowSkip = false,
  skipText = 'Skip',
  submitText = 'Confirm',
  onSkip,
  label = 'Select Date',
  minDate,
  maxDate,
  disabled = false,
  submitted = false,
  avatarUrl,
  avatarInitials = 'B',
}) => {
  const [internalValue, setInternalValue] = React.useState(controlledValue || '');

  const value = controlledValue !== undefined ? controlledValue : internalValue;

  const handleChange = (newValue: string) => {
    setInternalValue(newValue);
    onChange?.(newValue);
  };

  const handleSubmit = () => {
    if (value) {
      onSubmit?.(value);
    }
  };

  const handleSkip = () => {
    onSkip?.();
  };

  return (
    <MessageContainer>
      <Avatar>
        {avatarUrl ? (
          <img src={avatarUrl} alt="bot avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <span>{avatarInitials}</span>
        )}
      </Avatar>
      <ActivityCard>
        <CardContent>
          <MessageText>{text}</MessageText>
          <DatePicker
            label={label}
            value={value}
            onChange={handleChange}
            disabled={disabled || submitted}
            minDate={minDate || undefined}
            maxDate={maxDate || undefined}
          />
        </CardContent>
        {!submitted && (
          <CardActions sx={{ justifyContent: 'flex-end', gap: 1 }}>
            {allowSkip && (
              <Button size="small" variant="text" onClick={handleSkip} disabled={disabled}>
                {skipText}
              </Button>
            )}
            <Button size="small" variant="contained" onClick={handleSubmit} disabled={!value || disabled}>
              {submitText}
            </Button>
          </CardActions>
        )}
      </ActivityCard>
    </MessageContainer>
  );
};
